import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Apps as AppsIcon,
} from '@mui/icons-material';
import { Message } from '../../stores/chatStore';
import { AppService } from '../../services/api';

interface SaveToAppProps {
  message: Message;
  conversationId: string;
  onSaved?: (appId: string) => void;
}

const appService = new AppService();

const SaveToApp: React.FC<SaveToAppProps> = ({ message, conversationId, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: false,
    isOrganizationShared: false,
  });

  const handleOpen = () => {
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setFormData({
      name: '',
      description: '',
      isPublic: false,
      isOrganizationShared: false,
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('App name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const app = await appService.createAppFromConversation(
        conversationId,
        message.id,
        formData.name.trim(),
        formData.description.trim()
      );

      // Update visibility settings if needed
      if (formData.isPublic || formData.isOrganizationShared) {
        await appService.updateApp(app.id, {
          is_public: formData.isPublic,
          is_organization_shared: formData.isOrganizationShared,
        });
      }

      handleClose();
      onSaved?.(app.id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save as app');
    } finally {
      setSaving(false);
    }
  };

  // Only show for HTML content
  if (message.content_type !== 'html') {
    return null;
  }

  return (
    <>
      <Tooltip title="Save as App">
        <IconButton size="small" onClick={handleOpen} color="primary">
          <AppsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Save as App</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="App Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              autoFocus
              placeholder="My Amazing App"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe what this app does..."
            />

            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isOrganizationShared}
                    onChange={(e) => setFormData({ ...formData, isOrganizationShared: e.target.checked })}
                  />
                }
                label="Share with my organization"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                  />
                }
                label="Make publicly available"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.name.trim()}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save App'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SaveToApp;